import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Icon } from "../Icon";

import { TooltipProps } from "./types";

const tooltipAnimation = `
@keyframes tooltipPopup {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(8px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
`;

export default function Tooltip({
  text,
  position = "top",
  className,
  iconClassName,
  tooltipClassName,
  showIcon = true,
  icon = "questionCircle",
  children,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const animationFrameRef = useRef<number>();

  const clearPendingTimers = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  };

  const calculatePosition = () => {
    if (!triggerRef.current || !measureRef.current) return { left: 0, top: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = measureRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRect.height;
    const tooltipWidth = tooltipRect.width;

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipHeight - 8;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + 8;
        left = triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.left - tooltipWidth - triggerRect.width / 2 + 8;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - tooltipHeight / 2;
        left = triggerRect.right + 8;
        break;
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust horizontal position if tooltip would overflow
    if (left < 8) {
      left = 8;
    } else if (left + tooltipWidth > viewportWidth - 8) {
      left = viewportWidth - tooltipWidth - 8;
    }

    // Adjust vertical position if tooltip would overflow
    if (top < 8) {
      top = 8;
    } else if (top + tooltipHeight > viewportHeight - 8) {
      top = viewportHeight - tooltipHeight - 8;
    }

    return { left, top };
  };

  const handleMouseEnter = () => {
    clearPendingTimers();
    showTimeoutRef.current = setTimeout(() => {
      // First position the tooltip
      const pos = calculatePosition();
      setTooltipPosition(pos);
      setIsPositioned(true);
      // Then show it after a small delay
      animationFrameRef.current = requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }, 200);
  };

  const handleMouseLeave = () => {
    clearPendingTimers();
    setIsVisible(false);
    // Reset position after animation
    hideTimeoutRef.current = setTimeout(() => {
      setIsPositioned(false);
    }, 200);
  };

  useEffect(() => clearPendingTimers, []);

  useEffect(() => {
    if (isVisible) {
      const handleScroll = () => {
        const pos = calculatePosition();
        setTooltipPosition(pos);
      };
      const handleResize = () => {
        const pos = calculatePosition();
        setTooltipPosition(pos);
      };

      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isVisible]);

  if (!text) {
    return children;
  }

  return (
    <>
      <style>{tooltipAnimation}</style>
      <div
        ref={triggerRef}
        className={clsx("relative inline-block", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {children ||
          (showIcon ? (
            <Icon
              name={icon}
              className={clsx(
                "h-3 md:h-4 w-3 md:w-4 cursor-help text-gray-400 hover:text-gray-500",
                iconClassName,
              )}
            />
          ) : (
            <span className="cursor-help">{text}</span>
          ))}
      </div>

      {/* Hidden measurement div */}
      <div
        ref={measureRef}
        className={clsx(
          "fixed opacity-0 pointer-events-none",
          "rounded-md bg-gray-900 px-3 py-2",
          "text-[10px] md:text-xs font-medium text-white",
          "max-w-[280px] break-words whitespace-normal",
          tooltipClassName,
        )}
        style={{ visibility: "hidden" }}
      >
        {text}
      </div>

      {isPositioned &&
        createPortal(
          <div
            className={clsx(
              "fixed z-[9999]",
              "transition-all duration-200 ease-out",
              {
                "opacity-0 pointer-events-none scale-95 translate-y-2":
                  !isVisible,
                "opacity-100 pointer-events-auto scale-100 translate-y-0":
                  isVisible,
              },
            )}
            style={{
              left: `${tooltipPosition.left}px`,
              top: `${tooltipPosition.top}px`,
              transform: `translate3d(0, 0, 0)`,
              willChange: "transform, opacity",
            }}
          >
            <div
              className={clsx(
                "relative rounded-md bg-gray-900 px-3 py-2",
                "text-[10px] md:text-xs font-medium text-white shadow-lg",
                "max-w-[280px] break-words whitespace-normal",
                tooltipClassName,
              )}
              role="tooltip"
            >
              {text}
              <div
                className={clsx("absolute h-2 w-2 rotate-45 bg-gray-900", {
                  "-left-1 top-1/2 -translate-y-1/2": position === "right",
                  "-right-1 top-1/2 -translate-y-1/2": position === "left",
                  "left-1/2 -translate-x-1/2 -bottom-1": position === "top",
                  "left-1/2 -translate-x-1/2 -top-1": position === "bottom",
                })}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
